import { Injectable } from '@angular/core';

// iPhones capture HEIC/HEIF, which most browsers can't render, so a HEIC upload later shows as a
// broken image on the review screen. We detect HEIC by content (files often arrive mislabeled .jpg)
// and transcode to JPEG at upload time so stored bytes are always web-safe.
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'heif', 'mif1', 'msf1']);
const HEADER_BYTES = 12;

@Injectable({ providedIn: 'root' })
export class ImageConversionService {
  async toUploadableFile(file: File): Promise<File> {
    try {
      if (!(await this.isHeic(file))) {
        return file;
      }
      const jpeg = await this.convertHeicToJpeg(file);
      return new File([jpeg], this.toJpegName(file.name), { type: 'image/jpeg' });
    } catch {
      // On any failure keep the original file so the upload is never blocked by conversion.
      return file;
    }
  }

  async isHeic(blob: Blob): Promise<boolean> {
    let header: Uint8Array;
    try {
      header = await this.readHeader(blob);
    } catch {
      return false;
    }
    if (header.length < HEADER_BYTES) {
      return false;
    }
    const tag = (start: number): string => String.fromCharCode(...header.subarray(start, start + 4));
    return tag(4) === 'ftyp' && HEIF_BRANDS.has(tag(8));
  }

  // Isolated so tests can stub the heavy WASM decode; lazy import keeps heic2any out of the main bundle.
  protected async convertHeicToJpeg(blob: Blob): Promise<Blob> {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob, toType: 'image/jpeg' });
    return Array.isArray(converted) ? converted[0] : converted;
  }

  private toJpegName(name: string): string {
    return `${name.replace(/\.[^./\\]+$/, '')}.jpg`;
  }

  private readHeader(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(new Uint8Array(reader.result as ArrayBuffer));
      reader.onerror = (): void => reject(reader.error);
      reader.readAsArrayBuffer(blob.slice(0, HEADER_BYTES));
    });
  }
}
